/**
 * Telegram Mini App (TMA) detector + SDK helpers — L&D Super-UX Phase 6 PR 6.1.
 *
 * Цель: использовать обычное ERP-приложение Onboardix как Mini App внутри
 * Telegram-клиента. Запуск идёт по deep-link `t.me/<bot>?startapp=learning`
 * (см. BotFather → Mini App), Telegram отдаёт `window.Telegram.WebApp.initData`
 * — signed payload, который мы валидируем через POST /auth/telegram-webapp.
 *
 * Минимум зависимостей: используем native `window.Telegram.WebApp` API
 * без отдельного npm пакета (@twa-dev/sdk удвоит bundle и не нужен — мы
 * пользуемся всего парой методов: ready, expand, BackButton, themeParams).
 *
 * Telegram WebApp script сам устанавливается одним <script src=...> в
 * index.html (см. PR 6.1 — добавлен conditional loader там же).
 */

const TMA_QUERY_FLAG = 'tma'
const TMA_PATH_PREFIX = '/tma'

/**
 * Detect whether we're running inside a Telegram Mini App container.
 *
 * Hierarchy of signals (any truthy ⇒ TMA mode):
 *   1. `window.Telegram.WebApp.initData` непустой — самый надёжный indicator,
 *      Telegram заполняет его только когда iframe реально открыт в TG-клиенте.
 *   2. URL `?tma=1` — manual flag для локальной разработки (npm run dev в
 *      обычном браузере с подменой). Использовать с осторожностью — initData
 *      будет пустой, backend вернёт 401.
 *   3. URL starts with `/tma` (или `/erp/tma`) — route-level entry point.
 */
export function isInTelegramWebApp() {
    if (typeof window === 'undefined') return false
    const sdk = window.Telegram && window.Telegram.WebApp
    if (sdk && typeof sdk.initData === 'string' && sdk.initData.length > 0) {
        return true
    }
    try {
        const params = new URLSearchParams(window.location.search)
        if (params.get(TMA_QUERY_FLAG) === '1') return true
    } catch {
        /* ignore — non-browser env */
    }
    const path = window.location.pathname || ''
    return path.includes(TMA_PATH_PREFIX)
}

/**
 * Return the current Telegram WebApp SDK handle or null.
 * Callers should null-check before using methods.
 */
export function getTelegramSdk() {
    if (typeof window === 'undefined') return null
    return (window.Telegram && window.Telegram.WebApp) || null
}

/**
 * initData query-string for backend validation. May be empty for local dev
 * (`?tma=1` flag without real Telegram client). Returns "" in that case —
 * caller treats it as "no SDK initData available".
 */
export function getInitData() {
    const sdk = getTelegramSdk()
    return (sdk && sdk.initData) || ''
}

/**
 * Notify Telegram client that the Mini App content is ready. Triggers fade-out
 * of the loading overlay. Idempotent — safe to call multiple times.
 */
export function readyTelegramApp() {
    const sdk = getTelegramSdk()
    if (!sdk) return
    try {
        sdk.ready()
        // Expand to full height — без этого Mini App рендерится в половинку
        // экрана (default sheet mode). Для L&D-сценария «учусь в дороге»
        // нужен полный экран.
        if (typeof sdk.expand === 'function') sdk.expand()
    } catch {
        /* SDK method missing in older clients — ignore */
    }
}

/**
 * Wire the native Telegram BackButton to a callback.
 * Returns an unsubscribe function. No-op if SDK absent.
 */
export function wireBackButton(handler) {
    const sdk = getTelegramSdk()
    if (!sdk || !sdk.BackButton) return () => {}
    try {
        sdk.BackButton.show()
        sdk.BackButton.onClick(handler)
    } catch {
        return () => {}
    }
    return () => {
        try {
            sdk.BackButton.offClick(handler)
            sdk.BackButton.hide()
        } catch {
            /* ignore */
        }
    }
}

/**
 * Extract Telegram theme params (bg_color, text_color, etc.). Returns an
 * empty object when not in TMA — каллер может смело spread'ить.
 */
export function getThemeParams() {
    const sdk = getTelegramSdk()
    return (sdk && sdk.themeParams) || {}
}

/**
 * Признак «светлая/тёмная» из Telegram. Только признак — цвета Mini App
 * берёт из палитры Onboardix, а не из темы Telegram (решение владелицы).
 * Возвращает 'light' | 'dark' | null (старый клиент или SDK нет).
 */
export function getTelegramColorScheme() {
    const sdk = getTelegramSdk()
    const scheme = sdk && sdk.colorScheme
    return scheme === 'light' || scheme === 'dark' ? scheme : null
}

function callIfPresent(sdk, method, ...args) {
    if (!sdk || typeof sdk[method] !== 'function') return
    try {
        sdk[method](...args)
    } catch {
        /* метод есть, но клиент старый и отказал — красить нечем */
    }
}

/**
 * Красит шапку, фон под контентом и нижнюю полосу Telegram одним цветом,
 * чтобы на границе с Mini App не было шва. Telegram принимает только
 * `#RRGGBB`; всё остальное молча пропускаем.
 */
export function paintTelegramChrome(color) {
    if (!/^#[0-9a-f]{6}$/i.test(color || '')) return
    const sdk = getTelegramSdk()
    callIfPresent(sdk, 'setHeaderColor', color)
    callIfPresent(sdk, 'setBackgroundColor', color)
    callIfPresent(sdk, 'setBottomBarColor', color)
}

/**
 * Подписка на событие Telegram (`themeChanged`, `viewportChanged`…).
 * Возвращает функцию отписки. Нет SDK или onEvent — отписка-пустышка.
 */
function onTelegramEvent(name, handler) {
    const sdk = getTelegramSdk()
    if (!sdk || typeof sdk.onEvent !== 'function') return () => {}
    try {
        sdk.onEvent(name, handler)
    } catch {
        return () => {}
    }
    return () => {
        try {
            if (typeof sdk.offEvent === 'function') sdk.offEvent(name, handler)
        } catch {
            /* ignore */
        }
    }
}

/** Подписка на смену темы в Telegram. Возвращает функцию отписки. */
export function onTelegramThemeChanged(handler) {
    return onTelegramEvent('themeChanged', handler)
}

/**
 * Подписка на смену видимой высоты Mini App: открылась клавиатура,
 * человек потянул шторку. Возвращает функцию отписки.
 */
export function onTelegramViewportChanged(handler) {
    return onTelegramEvent('viewportChanged', handler)
}

/**
 * Видимая высота Mini App в пикселях по Telegram. Берётся устойчивая
 * (`viewportStableHeight`) — та, что не дёргается во время анимации
 * клавиатуры; нет её — текущая. Старый клиент или обычный браузер — null.
 */
export function getTelegramViewportHeight() {
    const sdk = getTelegramSdk()
    if (!sdk) return null
    const stable = Number(sdk.viewportStableHeight)
    if (stable > 0) return stable
    const current = Number(sdk.viewportHeight)
    return current > 0 ? current : null
}

/**
 * Вертикальные свайпы Telegram (Bot API 7.7+): свайп вниз сворачивает
 * Mini App. Пока открыт чат, их выключаем — иначе прокрутка ленты вверх
 * сворачивает приложение. Старый клиент без метода — ничего не делаем.
 */
export function setTelegramVerticalSwipes(enabled) {
    callIfPresent(getTelegramSdk(), enabled ? 'enableVerticalSwipes' : 'disableVerticalSwipes')
}

/**
 * Лёгкая вибрация. `selection` — переключение (вкладки), `light` — нажатие,
 * `success` / `error` / `warning` — итог действия (отправлено, отказ).
 * Нет HapticFeedback (старый клиент, обычный браузер) — ничего не делаем.
 */
export function haptic(kind = 'light') {
    const feedback = getTelegramSdk()?.HapticFeedback
    if (!feedback) return
    if (kind === 'selection') callIfPresent(feedback, 'selectionChanged')
    else if (kind === 'success' || kind === 'error' || kind === 'warning') {
        callIfPresent(feedback, 'notificationOccurred', kind)
    } else callIfPresent(feedback, 'impactOccurred', kind)
}

/**
 * Внутри ли настоящего клиента Telegram с его нижней кнопкой. Скрипт
 * telegram-web-app.js создаёт `MainButton` и в обычном браузере, но там
 * её никто не рисует — поэтому признак ещё и непустой `initData`.
 */
export function hasTelegramMainButton() {
    const sdk = getTelegramSdk()
    return !!(sdk && sdk.MainButton && getInitData())
}

/**
 * Нижняя кнопка Telegram («Отправить»). Ставит текст, цвет, доступность и
 * прогресс одним вызовом и показывает кнопку. Цвета — `#RRGGBB` из палитры
 * Onboardix; другой формат Telegram отвергает, поэтому такой просто не
 * передаём.
 */
export function setTelegramMainButton({ text, active = true, progress = false, color, textColor } = {}) {
    const button = getTelegramSdk()?.MainButton
    if (!button) return
    const params = { text, is_active: !!active, is_visible: true }
    if (/^#[0-9a-f]{6}$/i.test(color || '')) params.color = color
    if (/^#[0-9a-f]{6}$/i.test(textColor || '')) params.text_color = textColor
    callIfPresent(button, 'setParams', params)
    if (progress) callIfPresent(button, 'showProgress', false)
    else callIfPresent(button, 'hideProgress')
}

/** Спрятать нижнюю кнопку и снять с неё прогресс. */
export function hideTelegramMainButton() {
    const button = getTelegramSdk()?.MainButton
    if (!button) return
    callIfPresent(button, 'hideProgress')
    callIfPresent(button, 'hide')
}

/** Подписка на нажатие нижней кнопки. Возвращает функцию отписки. */
export function onTelegramMainButtonClick(handler) {
    const button = getTelegramSdk()?.MainButton
    if (!button || typeof button.onClick !== 'function') return () => {}
    try {
        button.onClick(handler)
    } catch {
        return () => {}
    }
    return () => callIfPresent(button, 'offClick', handler)
}

/**
 * Подтверждение «да/нет». В Telegram — его системный диалог
 * (`showConfirm`), без него — `window.confirm`. Возвращает Promise<boolean>.
 */
export function confirmInTelegram(message) {
    const sdk = getTelegramSdk()
    if (sdk && typeof sdk.showConfirm === 'function' && getInitData()) {
        return new Promise((resolve) => {
            try {
                sdk.showConfirm(message, (ok) => resolve(!!ok))
            } catch {
                resolve(typeof window.confirm === 'function' ? window.confirm(message) : true)
            }
        })
    }
    try {
        return Promise.resolve(typeof window.confirm === 'function' ? window.confirm(message) : true)
    } catch {
        return Promise.resolve(true)
    }
}

/**
 * Спрашивать ли Telegram подтверждение при закрытии всего Mini App
 * (свайп вниз, крестик). Включаем, пока в форме есть неотправленное.
 */
export function setTelegramClosingConfirmation(enabled) {
    const sdk = getTelegramSdk()
    callIfPresent(sdk, enabled ? 'enableClosingConfirmation' : 'disableClosingConfirmation')
}

/**
 * Открыть страницу ERP снаружи Mini App. В Telegram — `openLink`: ссылка
 * уходит в браузер телефона, а Mini App остаётся открытой. В обычном
 * браузере — новая вкладка.
 */
export function openExternalLink(url) {
    const sdk = getTelegramSdk()
    if (sdk && typeof sdk.openLink === 'function') {
        try {
            sdk.openLink(url)
            return
        } catch {
            /* старый клиент без openLink — откроем вкладкой ниже */
        }
    }
    if (typeof window !== 'undefined' && typeof window.open === 'function') {
        window.open(url, '_blank', 'noopener')
    }
}
